import React, { useState, useMemo } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { X, ArrowUpDown, Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

const SessionTable = ({ sessions, apps, onUpdateSession, onDeleteSession }) => {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [editedSessions, setEditedSessions] = useState({});
  const [sortConfig, setSortConfig] = useState({
    key: "date",
    direction: "desc",
  });
  const [selectedCasino, setSelectedCasino] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showStats, setShowStats] = useState(false);
  const itemsPerPage = 10;
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);

  const handleDeleteClick = (sessionId) => {
    setSessionToDelete(sessionId);
    setDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (sessionToDelete) {
      onDeleteSession(sessionToDelete);
      setSessionToDelete(null);
      setDeleteModalOpen(false);
    }
  };

  const cancelDelete = () => {
    setSessionToDelete(null);
    setDeleteModalOpen(false);
  };

  const handleEdit = (field, value, session) => {
    const parsedValue = [
      "startingSc",
      "betSize",
      "endingSc",
      "deposit",
    ].includes(field)
      ? parseFloat(value) || 0
      : field === "dateOut"
      ? value
      : parseInt(value) || 0;

    setEditedSessions((prev) => ({
      ...prev,
      [session._id]: {
        ...(prev[session._id] || {}),
        [field]: parsedValue,
        _id: session._id,
        appId: session.appId._id,
        userId: session.userId,
        gamesPlayed: session.gamesPlayed,
      },
    }));
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const filteredSessions = useMemo(() => {
    return sessions.filter((session) => {
      if (!selectedCasino) return true;
      return session.appId._id === selectedCasino;
    });
  }, [sessions, selectedCasino]);

  const sortedSessions = useMemo(() => {
    return [...filteredSessions].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === "casino") {
        aValue = a.appId.name;
        bValue = b.appId.name;
      }

      if (aValue === null) return 1;
      if (bValue === null) return -1;

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredSessions, sortConfig]);

  const paginatedSessions = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedSessions.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedSessions, currentPage]);

  const totalPages = Math.ceil(sortedSessions.length / itemsPerPage);

  const calculateRTP = (session) => {
    if (
      !session.startingSc ||
      !session.endingSc ||
      !session.betSize ||
      !session.spinsHands
    ) {
      return "-";
    }
    const totalBet = session.betSize * session.spinsHands;
    const totalNotPlayed = session.startingSc - totalBet;
    const netTotal = session.endingSc - totalNotPlayed;
    const rtp = (netTotal / totalBet) * 100;
    return `${rtp.toFixed(2)}%`;
  };

  const calculateWinningSession = (session) => {
    const editedSession = editedSessions[session._id] || {};
    const endingSc =
      editedSession.endingSc !== undefined
        ? editedSession.endingSc
        : session.endingSc || 0;
    const deposit =
      editedSession.deposit !== undefined
        ? editedSession.deposit
        : session.deposit || 0;

    const difference = endingSc - deposit;
    return difference > 0 ? difference.toFixed(2) : "0.00";
  };

  const calculateLosingSession = (session) => {
    const editedSession = editedSessions[session._id] || {};
    const endingSc =
      editedSession.endingSc !== undefined
        ? editedSession.endingSc
        : session.endingSc || 0;
    const deposit =
      editedSession.deposit !== undefined
        ? editedSession.deposit
        : session.deposit || 0;

    const difference = endingSc - deposit;
    return difference < 0 ? Math.abs(difference).toFixed(2) : "0.00";
  };

  const handleSaveChanges = async () => {
    setIsSaving(true);
    setSaveSuccess(false);

    try {
      const updatesArray = Object.values(editedSessions);
      await onUpdateSession(updatesArray);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
      setEditedSessions({});
    } catch (error) {
      console.error("Error saving sessions:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const toggleStats = () => {
    setShowStats(!showStats);
  };

  const exportToCSV = () => {
    const headers = showStats
      ? [
          "Casino",
          "Date In",
          "Date Out",
          "Games Played",
          "Starting SC",
          "Bet Size",
          "Spins/Hands",
          "Ending SC",
          "RTP",
        ]
      : [
          "Casino",
          "Date In",
          "Date Out",
          "Games Played",
          "Deposit",
          "Ending SC",
          "Winning Session",
          "Losing Session",
        ];

    const formatDateString = (date) => {
      return new Date(date).toLocaleString().replace(",", "");
    };

    const csvData = sortedSessions?.map((session) => {
      if (showStats) {
        return [
          session.appId.name,
          formatDateString(session.date),
          session.dateOut ? formatDateString(session.dateOut) : "",
          session.gamesPlayed,
          session.startingSc?.toFixed(2),
          session.betSize?.toFixed(2),
          session.spinsHands,
          session.endingSc?.toFixed(2),
          calculateRTP(session),
        ];
      } else {
        return [
          session.appId.name,
          formatDateString(session.date),
          session.dateOut ? formatDateString(session.dateOut) : "",
          session.gamesPlayed,
          session.deposit?.toFixed(2) || "0.00",
          session.endingSc?.toFixed(2) || "0.00",
          calculateWinningSession(session),
          calculateLosingSession(session),
        ];
      }
    });

    const csvContent = [
      headers.join(","),
      ...csvData?.map((row) => row.join(",")),
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `Sessions-${new Date().toISOString()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderSortIcon = (columnKey) => {
    return (
      <ArrowUpDown
        className={`inline ml-2 h-4 w-4 ${
          sortConfig.key === columnKey ? "opacity-100" : "opacity-50"
        } cursor-pointer`}
      />
    );
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleString("en-US");
  };

  const convertToDateTimeLocal = (dateString) => {
    const formattedDate = formatDateTime(dateString);
    if (!formattedDate) return "";

    const date = new Date(dateString);

    // Convert to datetime-local format: "YYYY-MM-DDTHH:MM"
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  return (
    <Card className="w-full bg-white">
      <CardHeader className="flex flex-col sm:flex-row items-center justify-between space-y-2 sm:space-y-0">
        <CardTitle>Sessions</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
          <select
            value={selectedCasino}
            onChange={(e) => setSelectedCasino(e.target.value)}
            className="w-full sm:w-48 p-2 border rounded bg-white"
          >
            <option value="">All Casinos</option>
            {apps?.map((app) => (
              <option key={app._id} value={app._id}>
                {app.name}
              </option>
            ))}
          </select>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <button
              onClick={handleSaveChanges}
              disabled={Object.keys(editedSessions).length === 0 || isSaving}
              className={`
                px-4 py-2 bg-blue-500 text-white rounded flex items-center justify-center gap-2 w-full sm:w-auto
                ${
                  Object.keys(editedSessions).length === 0 || isSaving
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-blue-600"
                }
                transition-colors
              `}
            >
              <span>Save Changes</span>
              {isSaving && (
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
              )}
              {saveSuccess && !isSaving && (
                <Check className="h-4 w-4 text-white" />
              )}
            </button>
            <button
              onClick={toggleStats}
              className={`px-4 py-2 ${
                showStats
                  ? "bg-green-500 hover:bg-green-600"
                  : "bg-gray-500 hover:bg-gray-600"
              } text-white rounded transition-colors w-full sm:w-auto`}
            >
              Stats: {showStats ? "On" : "Off"}
            </button>
            <button
              onClick={exportToCSV}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors w-full sm:w-auto"
            >
              Export CSV
            </button>
          </div>
        </div>

        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <table className="w-full min-w-[800px]">
            <thead>
              <tr className="bg-gray-50">
                <th
                  onClick={() => handleSort("casino")}
                  className="p-3 text-left font-medium cursor-pointer hover:bg-gray-100"
                >
                  Casino {renderSortIcon("casino")}
                </th>
                <th
                  onClick={() => handleSort("date")}
                  className="p-3 text-left font-medium cursor-pointer hover:bg-gray-100"
                >
                  Date In {renderSortIcon("date")}
                </th>
                <th
                  onClick={() => handleSort("dateOut")}
                  className="p-3 text-left font-medium cursor-pointer hover:bg-gray-100"
                >
                  Date Out {renderSortIcon("dateOut")}
                </th>
                <th
                  onClick={() => handleSort("gamesPlayed")}
                  className="p-3 text-left font-medium cursor-pointer hover:bg-gray-100"
                >
                  Games {renderSortIcon("gamesPlayed")}
                </th>
                {!showStats && (
                  <th
                    onClick={() => handleSort("deposit")}
                    className="p-3 text-right font-medium cursor-pointer hover:bg-gray-100"
                  >
                    Deposit {renderSortIcon("deposit")}
                  </th>
                )}
                {showStats && (
                  <>
                    <th
                      onClick={() => handleSort("startingSc")}
                      className="p-3 text-right font-medium cursor-pointer hover:bg-gray-100"
                    >
                      Start SC {renderSortIcon("startingSc")}
                    </th>
                    <th
                      onClick={() => handleSort("betSize")}
                      className="p-3 text-right font-medium cursor-pointer hover:bg-gray-100"
                    >
                      Bet {renderSortIcon("betSize")}
                    </th>
                    <th
                      onClick={() => handleSort("spinsHands")}
                      className="p-3 text-right font-medium cursor-pointer hover:bg-gray-100"
                    >
                      Spins {renderSortIcon("spinsHands")}
                    </th>
                  </>
                )}
                <th
                  onClick={() => handleSort("endingSc")}
                  className="p-3 text-right font-medium cursor-pointer hover:bg-gray-100"
                >
                  End SC {renderSortIcon("endingSc")}
                </th>
                {showStats && (
                  <th
                    onClick={() => handleSort("rtp")}
                    className="p-3 text-right font-medium cursor-pointer hover:bg-gray-100"
                  >
                    RTP {renderSortIcon("rtp")}
                  </th>
                )}
                {!showStats && (
                  <>
                    <th
                      onClick={() => handleSort("winningSession")}
                      className="p-3 text-right font-medium cursor-pointer hover:bg-gray-100"
                    >
                      Winning Session {renderSortIcon("winningSession")}
                    </th>
                    <th
                      onClick={() => handleSort("losingSession")}
                      className="p-3 text-right font-medium cursor-pointer hover:bg-gray-100"
                    >
                      Losing Session {renderSortIcon("losingSession")}
                    </th>
                  </>
                )}
                <th className="p-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedSessions?.map((session) => {
                const editedSession = editedSessions[session._id] || {};
                return (
                  <tr key={session._id} className="border-t">
                    <td className="p-3">{session.appId.name}</td>
                    <td className="p-3">{formatDateTime(session.date)}</td>
                    <td className="p-3">
                      <input
                        type="datetime-local"
                        value={
                          editedSession.dateOut !== undefined
                            ? editedSession.dateOut
                            : session.dateOut
                            ? convertToDateTimeLocal(session.dateOut)
                            : ""
                        }
                        onChange={(e) =>
                          handleEdit("dateOut", e.target.value, session)
                        }
                        className="p-1 border rounded"
                      />
                    </td>
                    <td className="p-3">{session.gamesPlayed}</td>
                    {!showStats && (
                      <td className="p-3 text-right">
                        <input
                          type="number"
                          value={
                            editedSession.deposit !== undefined
                              ? editedSession.deposit
                              : session.deposit || ""
                          }
                          onChange={(e) =>
                            handleEdit("deposit", e.target.value, session)
                          }
                          className="w-20 p-1 border rounded text-right"
                          step="0.01"
                        />
                      </td>
                    )}
                    {showStats && (
                      <>
                        <td className="p-3 text-right">
                          <input
                            type="number"
                            value={
                              editedSession.startingSc !== undefined
                                ? editedSession.startingSc
                                : session.startingSc || ""
                            }
                            onChange={(e) =>
                              handleEdit("startingSc", e.target.value, session)
                            }
                            className="w-20 p-1 border rounded text-right"
                            step="0.01"
                          />
                        </td>
                        <td className="p-3 text-right">
                          <input
                            type="number"
                            value={
                              editedSession.betSize !== undefined
                                ? editedSession.betSize
                                : session.betSize || ""
                            }
                            onChange={(e) =>
                              handleEdit("betSize", e.target.value, session)
                            }
                            className="w-20 p-1 border rounded text-right"
                            step="0.01"
                          />
                        </td>
                        <td className="p-3 text-right">
                          <input
                            type="number"
                            value={
                              editedSession.spinsHands !== undefined
                                ? editedSession.spinsHands
                                : session.spinsHands || ""
                            }
                            onChange={(e) =>
                              handleEdit("spinsHands", e.target.value, session)
                            }
                            className="w-20 p-1 border rounded text-right"
                          />
                        </td>
                      </>
                    )}
                    <td className="p-3 text-right">
                      <input
                        type="number"
                        value={
                          editedSession.endingSc !== undefined
                            ? editedSession.endingSc
                            : session.endingSc || ""
                        }
                        onChange={(e) =>
                          handleEdit("endingSc", e.target.value, session)
                        }
                        className="w-20 p-1 border rounded text-right"
                        step="0.01"
                      />
                    </td>
                    {showStats && (
                      <td className="p-3 text-right">
                        {calculateRTP(session)}
                      </td>
                    )}
                    {!showStats && (
                      <>
                        <td className="p-3 text-right">
                          {calculateWinningSession(session)}
                        </td>
                        <td className="p-3 text-right">
                          {calculateLosingSession(session)}
                        </td>
                      </>
                    )}
                    {/* <td className="p-3 text-center">
                      <button
                        onClick={() => onDeleteSession(session._id)}
                        className="p-1 text-red-500 hover:text-red-700 rounded-full hover:bg-red-100"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </td> */}
                    <td className="p-3 text-center">
                      <button
                        onClick={() => handleDeleteClick(session._id)}
                        className="p-1 text-red-500 hover:text-red-700 rounded-full hover:bg-red-100"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-center gap-2">
          <p className="text-sm text-gray-600">
            Total Sessions: {sortedSessions.length}
          </p>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-gray-100 rounded disabled:opacity-50 hover:bg-gray-200 transition-colors"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-gray-100 rounded disabled:opacity-50 hover:bg-gray-200 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      </CardContent>

      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent
          className="sm:max-w-md"
          onEscapeKeyDown={cancelDelete}
          style={{ backgroundColor: "#FFFFFF" }}
        >
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this session?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex justify-end space-x-2 pt-4">
            <button
              onClick={cancelDelete}
              className="px-4 py-2 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
            >
              No
            </button>
            <button
              onClick={confirmDelete}
              className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
            >
              Yes
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default SessionTable;
